import {
  faEnvelope,
  faPassport,
  faUserGroup,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Card, ResourceIdentifier, Tag, TagList, TaskBar, ValueList } from '.';
import config from '../config';
import { IdentityGroup } from '../types';
import formatDate from '../utilities/format-date';
import formatDuration from '../utilities/format-duration';
import {
  identityProviderIcon,
  identityProviderName,
} from '../utilities/identity-providers';
import './IdentityGroupCard.scss';

type IdentityGroupCardProps = {
  group: IdentityGroup;
  admin?: boolean;
  [key: string]: any;
};

export function IdentityGroupCard({
  group,
  admin,
  ...props
}: IdentityGroupCardProps): ReactElement {
  return (
    <Card className="identity-group-card" {...props}>
      <div className="corner-tags-container bottom">
        <Tag intent="info" icon={faEnvelope} show={group.requireEmail}>
          Email required
        </Tag>
        <Tag
          intent="info"
          icon={faPaperPlane}
          show={group.tokenDelivery === 'webhook'}
        >
          Webhook
        </Tag>
        {group.enabledProviders?.map(provider => (
          <Tag
            key={provider}
            intent="success"
            icon={identityProviderIcon(provider)}
            show
          >
            {identityProviderName(provider)}
          </Tag>
        ))}
      </div>
      <TaskBar
        left={
          <h2>
            <FontAwesomeIcon icon={faUserGroup} />
            <Link to={`/identity-groups/${group.id}`}>
              {group.name || 'Default group'}
            </Link>
          </h2>
        }
      />
      <ValueList
        values={{
          Id: <code>{group.id}</code>,
          Identities: (
            <Link
              to={
                group.name
                  ? `/identities?${new URLSearchParams({ group: group.name })}`
                  : '/identities'
              }
            >
              <FontAwesomeIcon icon={faPassport} /> {group.identityCount}
            </Link>
          ),
          Description: group.description || undefined,
          Tags: group.tags?.length ? <TagList tags={group.tags} /> : undefined,
          Sessions:
            group.sessionLifetime === null
              ? 'Never expire'
              : `Expire after ${formatDuration(group.sessionLifetime)}`,
          'Created by': admin ? (
            <ResourceIdentifier
              resourceType="user"
              resource={{
                id: group.user?.id ?? '',
                displayName: group.user?.displayName ?? '',
              }}
              copyable
            />
          ) : undefined,
          'Created at': formatDate(group.createdAt, config.pageDateFormat),
        }}
      />
    </Card>
  );
}
